import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isWeekend, addDays, subDays } from 'date-fns';
import { ro } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  User,
  Phone,
  X,
  Trash2,
  Lock,
  Unlock,
  Calendar,
  Stethoscope,
  ClipboardList,
  Printer
} from 'lucide-react';
import { getAppointments, createAppointment, updateAppointmentStatus, deleteAppointment, getPatients, getAvailableTimeSlots, getBlockedDays, blockDay, unblockDay, getConsultations } from '../services/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { DatePicker } from '../components/ui/date-picker';

// Generează sloturile orare (08:00-20:00, din 15 în 15 min)
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 8; hour < 20; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }
  }
  return slots;
};
const TIME_SLOTS = generateTimeSlots();

const PLACEHOLDER_IDS = new Set([
  '00000000-0000-0000-0000-000000000001',
  '00000000-0000-0000-0000-000000000002',
]);

const CalendarPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [blockedDays, setBlockedDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSelectedDateWeekend, setIsSelectedDateWeekend] = useState(false);
  const [isSelectedDateBlocked, setIsSelectedDateBlocked] = useState(false);
  const [selectedDateBlockedReason, setSelectedDateBlockedReason] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const { theme } = useTheme();
  const [calendarTab, setCalendarTab] = useState('programari');
  const [dayConsultations, setDayConsultations] = useState([]);
  const [loadingConsultations, setLoadingConsultations] = useState(false);
  
  const [formData, setFormData] = useState({
    patient_id: '',
    appointment_date: format(new Date(), 'yyyy-MM-dd'),
    appointment_time: '09:00',
    notes: ''
  });

  // Filter patients based on search (diacritic-insensitive)
  const filteredPatients = useMemo(() => {
    if (!patientSearch) return patients;
    const normalize = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const searchNorm = normalize(patientSearch);
    return patients.filter(p => 
      normalize(`${p.last_name} ${p.first_name}`).includes(searchNorm) ||
      p.cnp?.includes(patientSearch)
    );
  }, [patients, patientSearch]);

  // Reset search when dialog closes
  useEffect(() => {
    if (!dialogOpen) {
      setPatientSearch('');
    }
  }, [dialogOpen]);

  useEffect(() => {
    fetchData();
  }, [currentMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [appointmentsRes, patientsRes, blockedRes] = await Promise.all([
        getAppointments(),
        getPatients('', 1, 10000), // Get all patients for calendar dropdown
        getBlockedDays(format(currentMonth, 'yyyy-MM'))
      ]);
      console.log('Calendar data loaded:', {
        appointments: appointmentsRes.data?.length,
        patients: patientsRes.data?.patients?.length,
        blockedDays: blockedRes.data?.length
      });
      setAppointments(appointmentsRes.data || []);
      setPatients(patientsRes.data?.patients || []);
      setBlockedDays(blockedRes.data || []);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await getAppointments();
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    }
  };

  const fetchBlockedDays = async () => {
    try {
      const response = await getBlockedDays(format(currentMonth, 'yyyy-MM'));
      setBlockedDays(response.data);
    } catch (error) {
      console.error('Failed to fetch blocked days:', error);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await getAvailableTimeSlots(date);
      setAvailableSlots(response.data.slots || []);
      setIsSelectedDateWeekend(response.data.is_weekend || false);
      setIsSelectedDateBlocked(response.data.is_blocked || false);
      setSelectedDateBlockedReason(response.data.blocked_reason || null);
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setAvailableSlots(TIME_SLOTS.map(t => ({ time: t, available: true })));
      setIsSelectedDateWeekend(false);
      setIsSelectedDateBlocked(false);
    } finally {
      setLoadingSlots(false);
    }
  };

  const fetchConsultationsForDate = async (date) => {
    setLoadingConsultations(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await getConsultations(null, dateStr);
      setDayConsultations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
      setDayConsultations([]);
    } finally {
      setLoadingConsultations(false);
    }
  };

  const getPatientNameById = (patientId) => {
    const p = patients.find(pt => pt.id === patientId);
    return p ? `${p.last_name} ${p.first_name}` : 'Pacient necunoscut';
  };

  // Verifică dacă ziua selectată e blocată
  const checkSelectedDateBlocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const blocked = blockedDays.find(bd => bd.date === dateStr);
    setIsSelectedDateBlocked(!!blocked);
    setSelectedDateBlockedReason(blocked?.reason || null);
  };

  // Actualizează starea când se schimbă ziua selectată
  useEffect(() => {
    setIsSelectedDateWeekend(isWeekend(selectedDate));
    checkSelectedDateBlocked(selectedDate);
    fetchConsultationsForDate(selectedDate);
  }, [selectedDate, blockedDays]);

  // Încarcă sloturile când se schimbă data sau se deschide dialogul
  useEffect(() => {
    if (dialogOpen && formData.appointment_date) {
      fetchAvailableSlots(formData.appointment_date);
    }
  }, [dialogOpen, formData.appointment_date]);

  // Select first available slot when slots are loaded
  useEffect(() => {
    if (availableSlots.length > 0 && dialogOpen) {
      const firstAvailable = availableSlots.find(slot => slot.available);
      if (firstAvailable) {
        // Always select first available slot when slots change
        const currentSlotAvailable = availableSlots.find(s => s.time === formData.appointment_time && s.available);
        if (!currentSlotAvailable) {
          setFormData(prev => ({ ...prev, appointment_time: firstAvailable.time }));
        }
      } else {
        // No slots available - clear the selection
        setFormData(prev => ({ ...prev, appointment_time: '' }));
      }
    }
  }, [availableSlots, dialogOpen]);

  // Check if any slot is available
  const hasAvailableSlots = availableSlots.some(slot => slot.available);

  const handleBlockDay = async () => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    try {
      await blockDay(dateStr, blockReason || null);
      toast.success('Ziua a fost blocată');
      setBlockDialogOpen(false);
      setBlockReason('');
      fetchBlockedDays();
      checkSelectedDateBlocked(selectedDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la blocarea zilei');
    }
  };

  const handleUnblockDay = async () => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    try {
      await unblockDay(dateStr);
      toast.success('Ziua a fost deblocată');
      fetchBlockedDays();
      checkSelectedDateBlocked(selectedDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la deblocarea zilei');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.patient_id) {
      toast.error('Selectați un pacient');
      return;
    }
    try {
      await createAppointment(formData);
      toast.success('Programare creată cu succes!');
      setDialogOpen(false);
      setFormData({
        patient_id: '',
        appointment_date: format(selectedDate, 'yyyy-MM-dd'),
        appointment_time: '09:00',
        notes: ''
      });
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la crearea programării');
    }
  };

  const handleStatusChange = async (appointmentId, status) => {
    try {
      await updateAppointmentStatus(appointmentId, status);
      fetchAppointments();
      toast.success('Status actualizat');
    } catch (error) {
      toast.error('Eroare la actualizarea statusului');
    }
  };

  const handleDelete = async (appointmentId) => {
    if (!window.confirm('Sigur doriți să ștergeți această programare?')) return;
    try {
      await deleteAppointment(appointmentId);
      toast.success('Programare ștearsă');
      fetchAppointments();
    } catch (error) {
      toast.error('Eroare la ștergerea programării');
    }
  };

  // Zilele din luna curentă
  const getDaysInMonth = () => {
    return eachDayOfInterval({
      start: startOfMonth(currentMonth),
      end: endOfMonth(currentMonth)
    });
  };

  // Verifică dacă o zi e blocată manual
  const isDayBlocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedDays.some(bd => bd.date === dateStr);
  };

  const getAppointmentsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return appointments.filter(a => a.appointment_date === dateStr);
  };

  const selectedDateAppointments = getAppointmentsForDay(selectedDate);

  // Get the first day of the month and calculate padding days
  const firstDayOfMonth = startOfMonth(currentMonth);
  const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday
  const paddingDays = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1; // Adjust for Monday start

  // Get patient phone number
  const getPatientPhone = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    return patient?.phone || null;
  };

  // Navigate to next/prev working day (skip weekends)
  const goToNextWorkingDay = () => {
    let nextDay = addDays(selectedDate, 1);
    while (isWeekend(nextDay)) {
      nextDay = addDays(nextDay, 1);
    }
    setSelectedDate(nextDay);
    // Update month if needed
    if (!isSameMonth(nextDay, currentMonth)) {
      setCurrentMonth(nextDay);
    }
  };

  const goToPrevWorkingDay = () => {
    let prevDay = subDays(selectedDate, 1);
    while (isWeekend(prevDay)) {
      prevDay = subDays(prevDay, 1);
    }
    setSelectedDate(prevDay);
    // Update month if needed
    if (!isSameMonth(prevDay, currentMonth)) {
      setCurrentMonth(prevDay);
    }
  };

  // Sort appointments chronologically
  const sortedAppointments = useMemo(() => {
    return [...selectedDateAppointments].sort((a, b) => 
      a.appointment_time.localeCompare(b.appointment_time)
    );
  }, [selectedDateAppointments]);

  // Print today's schedule as an A4 landscape table
  const handlePrintSchedule = () => {
    const dateLabel = format(selectedDate, 'EEEE, d MMMM yyyy', { locale: ro });
    const esc = (s) => (s == null ? '' : String(s))
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const rows = sortedAppointments.map((a) => {
      const phone = getPatientPhone(a.patient_id) || '-';
      let bookedOn = '-';
      try { if (a.created_at) bookedOn = format(new Date(a.created_at), 'dd/MM/yyyy'); } catch (e) {}
      return `<tr>
        <td class="ora">${esc(a.appointment_time)}</td>
        <td>${esc(a.patient_name)}</td>
        <td>${esc(phone)}</td>
        <td>${esc(a.notes || '')}</td>
        <td>${esc(a.created_by_name || '')}</td>
        <td>${esc(bookedOn)}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="ro"><head><meta charset="utf-8"><title>Program ${esc(dateLabel)}</title>
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        * { box-sizing: border-box; }
        body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; }
        h1 { font-size: 18px; margin: 0 0 2px; }
        .sub { font-size: 12px; color: #555; margin: 0 0 14px; text-transform: capitalize; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #333; padding: 6px 8px; text-align: left; vertical-align: top; }
        th { background: #f0f0f0; font-weight: bold; }
        td.ora { font-weight: bold; white-space: nowrap; }
        tr:nth-child(even) td { background: #fafafa; }
        .empty { font-size: 13px; color: #666; margin-top: 20px; }
      </style></head><body>
        <h1>Programul zilei</h1>
        <p class="sub">${esc(dateLabel)} — ${sortedAppointments.length} programări</p>
        ${sortedAppointments.length === 0
          ? '<p class="empty">Nu există programări pentru această zi.</p>'
          : `<table>
              <thead><tr>
                <th style="width:9%">Ora</th>
                <th style="width:24%">Nume și prenume pacient</th>
                <th style="width:13%">Număr de mobil</th>
                <th style="width:26%">Notițe</th>
                <th style="width:16%">Programat de</th>
                <th style="width:12%">Data programării</th>
              </tr></thead>
              <tbody>${rows}</tbody>
            </table>`}
      </body></html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Permiteți ferestrele pop-up pentru a tipări programul');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={cn("w-8 h-8 border-4 border-t-transparent rounded-full animate-spin", theme.spinnerBorder)} />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* ==================== MOBILE VERSION ==================== */}
      <div className="md:hidden">
        {/* Mobile Header with Day Selector */}
        <div className="sticky top-0 z-10 bg-white pb-3 border-b">
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevWorkingDay}
              className="h-10 w-10"
              data-testid="mobile-prev-day"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            
            <div className="text-center">
              <p className="text-lg font-bold text-slate-900">
                {format(selectedDate, 'EEEE', { locale: ro })}
              </p>
              <p className="text-2xl font-bold text-slate-900">
                {format(selectedDate, 'd MMMM', { locale: ro })}
              </p>
              <p className="text-sm text-slate-500">
                {format(selectedDate, 'yyyy')}
              </p>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNextWorkingDay}
              className="h-10 w-10"
              data-testid="mobile-next-day"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>

          {/* Add appointment button for mobile - opens shared dialog */}
          <Button 
            className={cn("w-full", theme.primaryBg, theme.primaryBgHover)}
            onClick={() => {
              setFormData({ ...formData, appointment_date: format(selectedDate, 'yyyy-MM-dd') });
              setDialogOpen(true);
            }}
            data-testid="mobile-add-appointment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Programare Nouă
          </Button>
        </div>

        {/* Blocked Day Message - Mobile */}
        {(isSelectedDateWeekend || isSelectedDateBlocked) && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-600 font-medium">
                {isSelectedDateWeekend 
                  ? 'Weekend - Cabinet închis' 
                  : `Zi blocată${selectedDateBlockedReason ? `: ${selectedDateBlockedReason}` : ''}`
                }
              </p>
            </div>
          </div>
        )}

        {/* Mobile Tabs */}
        <div className="flex border-b border-slate-200 mt-3" data-testid="mobile-calendar-tabs">
          <button
            onClick={() => setCalendarTab('programari')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium border-b-2 transition-colors',
              calendarTab === 'programari'
                ? 'border-emerald-500 text-emerald-700'
                : 'border-transparent text-slate-500'
            )}
          >
            <ClipboardList className="w-4 h-4" />
            Programări ({sortedAppointments.length})
          </button>
          <button
            onClick={() => setCalendarTab('consultatii')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium border-b-2 transition-colors',
              calendarTab === 'consultatii'
                ? 'border-emerald-500 text-emerald-700'
                : 'border-transparent text-slate-500'
            )}
          >
            <Stethoscope className="w-4 h-4" />
            Consultații ({dayConsultations.length})
          </button>
        </div>

        {/* Mobile Tab Content: Programări */}
        {calendarTab === 'programari' && (
        <div className="space-y-3 pt-3">
          {sortedAppointments.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                {isSelectedDateWeekend || isSelectedDateBlocked 
                  ? 'Nu se pot face programări în această zi'
                  : 'Nu există programări pentru această zi'
                }
              </p>
            </div>
          ) : (
            sortedAppointments.map((appointment) => {
              const phone = getPatientPhone(appointment.patient_id);
              const isPlaceholder = PLACEHOLDER_IDS.has(appointment.patient_id);
              const statusBorder = isPlaceholder ? 'border-l-slate-300' :
                appointment.status === 'completed' ? 'border-l-emerald-500' :
                appointment.status === 'cancelled' ? 'border-l-red-500' :
                'border-l-amber-400';
              return (
                <div 
                  key={appointment.id}
                  className={cn("bg-white border rounded-xl p-4 shadow-sm border-l-4", statusBorder)}
                  data-testid={`mobile-appointment-${appointment.id}`}
                >
                  {/* Time and Status */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className={cn("w-5 h-5", theme.primaryText)} />
                      <span className="text-xl font-bold">{appointment.appointment_time}</span>
                    </div>
                    <Badge className={cn(
                      "text-xs",
                      appointment.status === 'completed' && "bg-emerald-100 text-emerald-700",
                      appointment.status === 'scheduled' && "bg-amber-100 text-amber-700",
                      appointment.status === 'cancelled' && "bg-red-100 text-red-700"
                    )}>
                      {appointment.status === 'scheduled' ? 'Programat' : 
                       appointment.status === 'completed' ? 'Finalizat' : 'Anulat'}
                    </Badge>
                  </div>

                  {/* Patient Name and Quick Call Button */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <User className="w-5 h-5 text-slate-400" />
                      <span className="text-lg font-semibold text-slate-900">
                        {appointment.patient_name}
                      </span>
                    </div>
                    {/* Quick Call Button */}
                    {phone && (
                      <a 
                        href={`tel:${phone}`}
                        className={cn(
                          "flex items-center gap-2 px-4 py-2 rounded-full text-white font-medium shadow-md active:scale-95 transition-transform",
                          theme.primaryBg, theme.primaryBgHover
                        )}
                        data-testid={`mobile-call-${appointment.id}`}
                      >
                        <Phone className="w-5 h-5" />
                        <span>Sună</span>
                      </a>
                    )}
                  </div>

                  {/* Phone Number Display (if exists) */}
                  {phone && (
                    <p className="text-sm text-slate-500 mb-2 ml-7">
                      Tel: {phone}
                    </p>
                  )}

                  {/* Notes */}
                  {appointment.notes && (
                    <p className="text-sm text-slate-500 mb-3 bg-slate-50 p-2 rounded">
                      {appointment.notes}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t">
                    {/* Status change - only for admin/medic */}
                    {user?.role !== 'asistent' && (
                      <Select
                        value={appointment.status}
                        onValueChange={(value) => handleStatusChange(appointment.id, value)}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="scheduled">Programat</SelectItem>
                          <SelectItem value="completed">Finalizat</SelectItem>
                          <SelectItem value="cancelled">Anulat</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {/* Delete button - all roles can delete */}
                    <Button
                      variant="destructive"
                      size="icon"
                      className="h-10 w-10 min-w-[40px] flex-shrink-0"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete(appointment.id);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete(appointment.id);
                      }}
                      data-testid={`mobile-delete-appointment-${appointment.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        )}

        {/* Mobile Tab Content: Consultații */}
        {calendarTab === 'consultatii' && (
        <div className="space-y-3 pt-3">
          {loadingConsultations ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : dayConsultations.length === 0 ? (
            <div className="text-center py-12">
              <Stethoscope className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nu există consultații pentru această zi</p>
            </div>
          ) : (
            dayConsultations.map((consultation) => (
              <div
                key={consultation.id}
                className="bg-white border rounded-xl p-4 shadow-sm"
                data-testid={`mobile-consultation-${consultation.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className={cn("w-5 h-5", theme.primaryText)} />
                    <span className="text-xl font-bold">{consultation.consultation_time}</span>
                  </div>
                  <Badge className="text-xs bg-emerald-100 text-emerald-700">Consultație</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-slate-400" />
                  <span className="text-lg font-semibold text-slate-900">
                    {consultation.patient_name || getPatientNameById(consultation.patient_id)}
                  </span>
                </div>
                {consultation.doctor_name && (
                  <p className="text-sm text-slate-500 mt-2 ml-7">Dr. {consultation.doctor_name}</p>
                )}
              </div>
            ))
          )}
        </div>
        )}
      </div>

      {/* ==================== DESKTOP VERSION ==================== */}
      <div className="hidden md:block">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Calendar</h1>
            <p className="text-slate-500 mt-1">Gestionați programările pacienților</p>
          </div>
          {/* Asistentul poate face programări din calendar */}
          <Button 
            className={cn(theme.primaryBg, theme.primaryBgHover)}
            onClick={() => {
              setFormData({ ...formData, appointment_date: format(selectedDate, 'yyyy-MM-dd') });
              setDialogOpen(true);
            }}
            data-testid="add-appointment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Programare Nouă
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendar Grid - Made smaller */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-lg font-semibold capitalize">
                {format(currentMonth, 'MMMM yyyy', { locale: ro })}
              </CardTitle>
              <div className="flex items-center gap-1">
                <Button 
                  variant="outline" 
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  data-testid="prev-month-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="h-8 px-2 text-xs"
                  onClick={() => {
                    setCurrentMonth(new Date());
                    setSelectedDate(new Date());
                  }}
                >
                  Azi
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  data-testid="next-month-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Week Days Header - Romanian, Monday first */}
              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'].map((day, index) => (
                  <div 
                    key={day} 
                    className={cn(
                      "text-center text-xs font-medium py-1",
                      index >= 5 ? "text-red-500" : "text-slate-500"
                    )}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days - Smaller cells */}
              <div className="grid grid-cols-7 gap-0.5">
                {/* Padding days */}
                {Array.from({ length: paddingDays }).map((_, i) => (
                  <div key={`padding-${i}`} className="aspect-square" />
                ))}
                
                {/* Actual days */}
                {getDaysInMonth().map((day) => {
                  const dayAppointments = getAppointmentsForDay(day);
                  const isSelected = isSameDay(day, selectedDate);
                  const isToday = isSameDay(day, new Date());
                  const isDayWeekend = isWeekend(day);
                  const isDayManuallyBlocked = isDayBlocked(day);
                  const isDayUnavailable = isDayWeekend || isDayManuallyBlocked;
                  
                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => setSelectedDate(day)}
                      className={cn(
                        'aspect-square p-0.5 rounded transition-all relative text-xs',
                        'focus:outline-none focus:ring-1 focus:ring-emerald-500',
                        // Weekend or blocked day styling - red background
                        isDayUnavailable && !isSelected && 'bg-red-50 text-red-400 hover:bg-red-100',
                        // Normal day hover
                        !isDayUnavailable && !isSelected && 'hover:bg-slate-100',
                        // Selected day - use theme color
                        isSelected && !isDayUnavailable && cn(theme.primaryBg, 'text-white', theme.primaryBgHover),
                        isSelected && isDayUnavailable && 'bg-red-500 text-white hover:bg-red-600',
                        // Today
                        isToday && !isSelected && !isDayUnavailable && 'bg-slate-100 font-semibold',
                        isToday && !isSelected && isDayUnavailable && 'bg-red-100 font-semibold',
                        // Different month
                        !isSameMonth(day, currentMonth) && !isDayUnavailable && 'text-slate-300',
                        !isSameMonth(day, currentMonth) && isDayUnavailable && 'text-red-200'
                      )}
                      data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                    >
                      <span>{format(day, 'd')}</span>
                      {/* Blocked indicator */}
                      {isDayManuallyBlocked && !isDayWeekend && (
                        <Lock className="w-2 h-2 absolute top-0 right-0 text-red-400" />
                      )}
                      {dayAppointments.length > 0 && (
                        <div className={cn(
                          'absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5',
                        )}>
                          {dayAppointments.slice(0, 3).map((apt, i) => (
                            <div 
                              key={i} 
                              className={cn(
                                'w-1 h-1 rounded-full',
                                isSelected ? 'bg-white' :
                                  apt.status === 'completed' ? 'bg-emerald-500' :
                                  apt.status === 'cancelled' ? 'bg-red-500' :
                                  'bg-amber-400'
                              )}
                            />
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Selected Day Details */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <div>
                <CardTitle className="text-lg capitalize">
                  {format(selectedDate, 'EEEE, d MMMM yyyy', { locale: ro })}
                </CardTitle>
                <p className="text-sm text-slate-500 mt-0.5">
                  {selectedDateAppointments.length} programări
                </p>
              </div>
              {/* Print schedule + Block/Unblock day */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrintSchedule}
                  data-testid="print-schedule-btn"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Tipărește programul de azi
                </Button>
                {user?.role !== 'asistent' && !isSelectedDateWeekend && (
                  <>
                    {isSelectedDateBlocked ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleUnblockDay}
                        className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
                      >
                        <Unlock className="w-4 h-4 mr-1" />
                        Deblochează
                      </Button>
                    ) : (
                      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-red-600 border-red-600 hover:bg-red-50">
                            <Lock className="w-4 h-4 mr-1" />
                            Blochează
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Blochează ziua {format(selectedDate, 'd MMMM yyyy', { locale: ro })}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 pt-4">
                            <div className="space-y-2">
                              <Label>Motiv (opțional)</Label>
                              <Input
                                value={blockReason}
                                onChange={(e) => setBlockReason(e.target.value)}
                                placeholder="Ex: Concediu, Sărbătoare, etc."
                              />
                            </div>
                            <Button onClick={handleBlockDay} className="w-full bg-red-600 hover:bg-red-700">
                              Blochează Ziua
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Weekend/Blocked warning */}
              {(isSelectedDateWeekend || isSelectedDateBlocked) && (
                <div className={cn(
                  "p-3 rounded-lg mb-4",
                  isSelectedDateWeekend ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
                )}>
                  <p className="font-medium flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    {isSelectedDateWeekend 
                      ? "Weekend - programările nu sunt permise" 
                      : `Zi blocată${selectedDateBlockedReason ? `: ${selectedDateBlockedReason}` : ''}`
                    }
                  </p>
                </div>
              )}

              {/* Tabs: Programări / Consultații */}
              <div className="flex border-b border-slate-200 mb-4" data-testid="calendar-tabs">
                <button
                  onClick={() => setCalendarTab('programari')}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                    calendarTab === 'programari'
                      ? 'border-emerald-500 text-emerald-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  )}
                  data-testid="tab-programari"
                >
                  <ClipboardList className="w-4 h-4" />
                  Programări
                  <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{sortedAppointments.length}</Badge>
                </button>
                <button
                  onClick={() => setCalendarTab('consultatii')}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                    calendarTab === 'consultatii'
                      ? 'border-emerald-500 text-emerald-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  )}
                  data-testid="tab-consultatii"
                >
                  <Stethoscope className="w-4 h-4" />
                  Consultații
                  <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{dayConsultations.length}</Badge>
                </button>
              </div>
              
              {/* Tab Content: Programări */}
              {calendarTab === 'programari' && (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {sortedAppointments.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>Nu există programări pentru această zi</p>
                  </div>
                ) : (
                  sortedAppointments.map((appointment) => {
                    const phone = getPatientPhone(appointment.patient_id);
                    const isPlaceholder = PLACEHOLDER_IDS.has(appointment.patient_id);
                    const statusBorder = isPlaceholder ? 'border-l-slate-300' :
                      appointment.status === 'completed' ? 'border-l-emerald-500' :
                      appointment.status === 'cancelled' ? 'border-l-red-500' :
                      'border-l-amber-400';
                    return (
                      <div
                        key={appointment.id}
                        className={cn("p-4 rounded-xl space-y-2 bg-slate-50 border-l-4", statusBorder)}
                        data-testid={`appointment-${appointment.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className="font-semibold">{appointment.appointment_time}</span>
                          </div>
                          {/* Delete button - all roles can delete */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-500"
                            onClick={() => handleDelete(appointment.id)}
                            data-testid={`delete-appointment-${appointment.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div 
                          className={cn(
                            "flex items-center gap-2",
                            PLACEHOLDER_IDS.has(appointment.patient_id)
                              ? "cursor-default"
                              : "cursor-pointer hover:text-emerald-600"
                          )}
                          onClick={() => !PLACEHOLDER_IDS.has(appointment.patient_id) && navigate(`/patients/${appointment.patient_id}`)}
                        >
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{appointment.patient_name}</span>
                        </div>

                        {/* Phone number */}
                        {phone && (
                          <a 
                            href={`tel:${phone}`}
                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                          >
                            <Phone className="w-4 h-4" />
                            <span>{phone}</span>
                          </a>
                        )}
                        
                        {appointment.notes && (
                          <p className="text-sm text-slate-500">{appointment.notes}</p>
                        )}
                        
                        {appointment.created_by_name && (
                          <p className="text-xs text-slate-400 italic">
                            Programat de: {appointment.created_by_name}
                          </p>
                        )}
                        
                        {user?.role !== 'asistent' ? (
                          <div className="flex items-center gap-2 pt-2">
                            <Select
                              value={appointment.status}
                              onValueChange={(value) => handleStatusChange(appointment.id, value)}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="scheduled">Programat</SelectItem>
                                <SelectItem value="completed">Finalizat</SelectItem>
                                <SelectItem value="cancelled">Anulat</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div className="pt-2">
                            <Badge className={cn(
                              "text-xs",
                              appointment.status === 'completed' && "bg-emerald-100 text-emerald-700",
                              appointment.status === 'scheduled' && "bg-amber-100 text-amber-700",
                              appointment.status === 'cancelled' && "bg-red-100 text-red-700"
                            )}>
                              {appointment.status === 'scheduled' ? 'Programat' : 
                               appointment.status === 'completed' ? 'Finalizat' : 'Anulat'}
                            </Badge>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              )}

              {/* Tab Content: Consultații */}
              {calendarTab === 'consultatii' && (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {loadingConsultations ? (
                  <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : dayConsultations.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Stethoscope className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>Nu există consultații pentru această zi</p>
                  </div>
                ) : (
                  dayConsultations.map((consultation) => (
                    <div
                      key={consultation.id}
                      className="p-4 rounded-xl space-y-2 bg-slate-50"
                      data-testid={`consultation-${consultation.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className="font-semibold">{consultation.consultation_time}</span>
                        </div>
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Consultație</Badge>
                      </div>
                      
                      <div 
                        className="flex items-center gap-2 cursor-pointer hover:text-emerald-600"
                        onClick={() => navigate(`/patients/${consultation.patient_id}?tab=consultations&cid=${consultation.id}`)}
                        data-testid={`consultation-patient-link-${consultation.id}`}
                      >
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{consultation.patient_name || getPatientNameById(consultation.patient_id)}</span>
                      </div>

                      {consultation.doctor_name && (
                        <p className="text-xs text-slate-400 italic">
                          Dr. {consultation.doctor_name}
                        </p>
                      )}

                      {consultation.diagnosis_ids?.length > 0 && (
                        <p className="text-xs text-slate-500">
                          {consultation.diagnosis_ids.length} diagnostic{consultation.diagnosis_ids.length > 1 ? 'e' : ''}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Shared Dialog for both mobile and desktop */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Programare Nouă</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Pacient *</Label>
              <div className="relative">
                <Input
                  placeholder="Căutați după nume sau CNP..."
                  value={patientSearch || (formData.patient_id ? 
                    (() => {
                      const selected = patients.find(p => p.id === formData.patient_id);
                      return selected ? `${selected.last_name} ${selected.first_name}` : '';
                    })() : ''
                  )}
                  onChange={(e) => {
                    setPatientSearch(e.target.value);
                    if (formData.patient_id) {
                      setFormData({ ...formData, patient_id: '' });
                    }
                  }}
                  onFocus={() => {
                    if (formData.patient_id) {
                      setPatientSearch('');
                    }
                  }}
                  data-testid="appointment-patient-search"
                />
                {patientSearch && !formData.patient_id && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredPatients.length === 0 ? (
                      <div className="p-3 text-sm text-slate-500 text-center">
                        Niciun pacient găsit
                      </div>
                    ) : (
                      filteredPatients.map((p) => (
                        <div
                          key={p.id}
                          className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm"
                          onClick={() => {
                            setFormData({ ...formData, patient_id: p.id });
                            setPatientSearch('');
                          }}
                        >
                          <span className="font-medium">{p.last_name} {p.first_name}</span>
                          <span className="text-slate-400 ml-2">CNP: {p.cnp}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              {formData.patient_id && (
                <p className="text-xs text-emerald-600">
                  ✓ Pacient selectat: {patients.find(p => p.id === formData.patient_id)?.last_name} {patients.find(p => p.id === formData.patient_id)?.first_name}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data *</Label>
                <DatePicker
                  value={formData.appointment_date}
                  onChange={(date) => setFormData({ ...formData, appointment_date: date })}
                  minDate={format(new Date(), 'yyyy-MM-dd')}
                  placeholder="Selectați data"
                  data-testid="appointment-date-input"
                />
                <p className="text-xs text-slate-500">Programările pot fi doar în viitor</p>
              </div>
              <div className="space-y-2">
                <Label>Ora * {availableSlots.length > 0 && (
                  <span className="text-xs text-slate-400">({availableSlots[0].time} - {availableSlots[availableSlots.length - 1].time})</span>
                )}</Label>
                <Select 
                  value={formData.appointment_time}
                  onValueChange={(value) => setFormData({ ...formData, appointment_time: value })}
                >
                  <SelectTrigger data-testid="appointment-time-select" disabled={!hasAvailableSlots && !loadingSlots}>
                    <SelectValue placeholder={!hasAvailableSlots && !loadingSlots ? "Nu există sloturi disponibile" : "Selectați ora"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {loadingSlots ? (
                      <SelectItem value="loading" disabled>Se încarcă...</SelectItem>
                    ) : availableSlots.length > 0 ? (
                      availableSlots.map((slot) => (
                        <SelectItem 
                          key={slot.time} 
                          value={slot.time}
                          disabled={!slot.available}
                          className={!slot.available ? 'text-red-400 line-through' : ''}
                        >
                          {slot.time} {!slot.available && '(ocupat)'}
                        </SelectItem>
                      ))
                    ) : (
                      TIME_SLOTS.map((time) => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {!hasAvailableSlots && !loadingSlots && availableSlots.length > 0 && (
                  <p className="text-xs text-red-500 mt-1">Nu mai există sloturi disponibile pentru această zi</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notițe</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                data-testid="appointment-notes-input"
              />
            </div>
            <Button 
              type="submit" 
              className={cn("w-full", theme.primaryBg, theme.primaryBgHover)}
              disabled={!hasAvailableSlots || !formData.patient_id || !formData.appointment_time}
            >
              Creează Programarea
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
